import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { NATIVE_LANGUAGE_OPTIONS } from '../../../model/ai-personalization.model';

/**
 * Écran de bienvenue + informations manquantes (maquette 2).
 * Salutation, langue maternelle et date de naissance (facultative) ;
 * champs optionnels supplémentaires si le profil est incomplet.
 */
@Component({
    selector: 'lib-ai-exam-onboarding-welcome-profile',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        TranslateModule,
    ],
    templateUrl: './AI-exam-onboarding-welcome-profile.component.html',
    styleUrls: ['./AI-exam-onboarding-welcome-profile.component.scss'],
    providers: [
        provideMomentDateAdapter({
            parse: { dateInput: 'DD/MM/yyyy' },
            display: {
                dateInput: 'DD/MM/yyyy',
                monthYearLabel: 'MMMM YYYY',
                dateA11yLabel: 'DD/MM/yyyy',
                monthYearA11yLabel: 'MMMM yyyy',
            },
        }),
        { provide: MAT_DATE_LOCALE, useValue: 'fr' },
    ],
})
export class AIExamOnboardingWelcomeProfileComponent implements OnChanges {
    @Input() firstName = '';
    @Input() lastName = '';
    @Input() languageName = '';
    @Input() nativeLanguage: string | null = null;
    @Input() loading = false;
    /** Affiche la case déclaration infos perso (LILATE). */
    @Input() showLegalCheckbox = false;
    @Input() legalAlreadyAccepted = false;
    @Input() showExtraFields = false;
    /** Date de naissance déjà connue (profil prérempli). */
    @Input() hasBirthdate = false;

    @Output() nativeLanguageChange = new EventEmitter<string>();
    @Output() continue = new EventEmitter<{
        birthdate?: string | Date | null;
        firstName?: string;
        lastName?: string;
        email?: string;
        mobilePhone?: string;
        gender?: string;
        personalAccepted?: boolean;
    }>();

    readonly nativeLanguageOptions = NATIVE_LANGUAGE_OPTIONS;
    /** Aligné sur la validation backend (année >= 1900). */
    readonly minBirthdate = AIExamOnboardingWelcomeProfileComponent.createDate(1900, 0, 1);
    readonly maxBirthdate = AIExamOnboardingWelcomeProfileComponent.createToday();
    /** Point de départ du calendrier (vue multi-year) : ~35 ans en arrière. */
    readonly birthdatePickerStartAt = AIExamOnboardingWelcomeProfileComponent.createDate(
        new Date().getFullYear() - 35, 0, 1,
    );

    extraForm = new FormGroup({
        firstName: new FormControl('', Validators.required),
        lastName: new FormControl('', Validators.required),
        email: new FormControl('', [Validators.required, Validators.email]),
        mobilePhone: new FormControl('', Validators.required),
        gender: new FormControl('', Validators.required),
        birthdate: new FormControl<Date | null>(null, [
            AIExamOnboardingWelcomeProfileComponent.birthdateRangeValidator(
                AIExamOnboardingWelcomeProfileComponent.createDate(1900, 0, 1),
                AIExamOnboardingWelcomeProfileComponent.createToday(),
            ),
        ]),
        personalAccepted: new FormControl(false, Validators.requiredTrue),
    });

    /**
     * La déclaration de données personnelles reste obligatoire dès que la case est affichée.
     * Sans case (parcours FlashLevel), l'exiger bloquerait « Continuer » sans message d'erreur.
     */
    ngOnChanges(): void {
        const control = this.extraForm.controls.personalAccepted;
        if (this.showLegalCheckbox && !this.legalAlreadyAccepted) {
            control.addValidators(Validators.requiredTrue);
        } else {
            control.removeValidators(Validators.requiredTrue);
        }
        control.updateValueAndValidity();
    }

    /** Date normalisée à minuit (comparaisons jour par jour). */
    private static createDate(year: number, month: number, day: number): Date {
        const date = new Date(year, month, day);
        date.setHours(0, 0, 0, 0);
        return date;
    }

    /** Date du jour normalisée à minuit. */
    private static createToday(): Date {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }

    /** Rejette les dates hors plage autorisée (1900 – aujourd'hui). */
    private static birthdateRangeValidator(min: Date, max: Date): ValidatorFn {
        return (control) => {
            const value = control.value as Date | null;
            if (!value) {
                return null;
            }
            const selected = new Date(value);
            selected.setHours(0, 0, 0, 0);
            if (selected < min || selected > max) {
                return { birthdateOutOfRange: true };
            }
            return null;
        };
    }

    /** Met à jour le formulaire étendu depuis le parent. */
    patchExtraForm(values: Record<string, unknown>): void {
        const patch = { ...values };
        if (patch.birthdate && typeof patch.birthdate === 'string') {
            patch.birthdate = new Date(patch.birthdate);
        }
        this.extraForm.patchValue(patch);
        Object.keys(this.extraForm.controls).forEach((key) => {
            const control = this.extraForm.get(key);
            if (control?.value && key !== 'personalAccepted') {
                control.disable();
            }
        });
        if (this.legalAlreadyAccepted) {
            this.extraForm.get('personalAccepted')?.setValue(true);
            this.extraForm.get('personalAccepted')?.disable();
        }
    }

    onNativeLanguageChange(value: string): void {
        this.nativeLanguageChange.emit(value);
    }

    onContinue(): void {
        if (this.showExtraFields) {
            if (this.extraForm.invalid) {
                return;
            }
            const raw = this.extraForm.getRawValue();
            this.continue.emit({
                birthdate: raw.birthdate,
                firstName: raw.firstName || undefined,
                lastName: raw.lastName || undefined,
                email: raw.email || undefined,
                mobilePhone: raw.mobilePhone || undefined,
                gender: raw.gender || undefined,
                personalAccepted: raw.personalAccepted ?? undefined,
            });
            return;
        }

        const birthdate = this.extraForm.get('birthdate')?.value;
        this.continue.emit({
            birthdate: birthdate || null,
            personalAccepted: this.extraForm.get('personalAccepted')?.value ?? undefined,
        });
    }

    get greetingName(): string {
        return this.firstName;
    }

    isContinueDisabled(): boolean {
        if (this.loading) {
            return true;
        }
        if (!this.nativeLanguage) {
            return true;
        }
        if (this.showLegalCheckbox && !this.legalAlreadyAccepted
            && this.extraForm.get('personalAccepted')?.value !== true) {
            return true;
        }
        if (this.showExtraFields) {
            return this.extraForm.invalid;
        }
        return false;
    }
}
